import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Link, useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import { getError, formatEuro } from '../utils';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import CheckoutSteps from '../components/general/CheckoutSteps';
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import { Container, Modal } from 'react-bootstrap';
import { getFromIndexedDB } from '../indexedDB';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Alert from 'react-bootstrap/Alert';
import {
  migrateDesignIfNeeded,
  getMigratedDesignAssetFromIndexedDB,
} from '../utils/customizerCompat';
import { prepareKeycapDataForUpload } from '../utils/designUpload';
import {
  cartUserIconAssetIds,
  verifyUserIconAssetAccess,
} from '../utils/userIconAccess';
import { calculateOrderTotals } from '../utils/orderTotals';
import {
  isAddressComplete,
  isSameAddress,
  getCartIntegrityReasonKeys,
  findShippingZone,
  holidayConfirmationRequired,
} from '../utils/checkout';
import {
  paypalOrderBody,
  placedCartItem,
  orderBody,
} from '../utils/checkoutOrder';
import {
  groupCartItemsByLayoutProfile,
  buildSimpleStockFailureMessages,
  buildComplexStockFailureMessages,
} from '../utils/stockCheck';
import { formatDate } from '../utils';

const reducer = (state, action) => {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'PAY_REQUEST':
      return { ...state, loading: true };
    case 'PAY_SUCCESS':
      return { ...state, loading: false };
    case 'PAY_FAIL':
      return { ...state, loading: false };
    default:
      return state;
  }
};

export default function PlaceOrderScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen

  const currentLanguage = i18n.language; // Gets the current language

  const bucketName =
    process.env.NODE_ENV === 'production'
      ? 'custom-keycaps-prod-designs'
      : 'custom-keycaps-designs'; // conditionally seting the S3 bucket for design thumbnails, based on the NODE_ENV

  const [{ loading }, dispatch] = useReducer(reducer, {
    // default value:
    loading: false,
  }); // useReducer function instead of useState hook, to manage complex state. Gets the states (loading) and dispatch, to dispatch the actions from the reducer above

  const [orderDone, setOrderDone] = useState(false); // a state tracking if the order has been placed (so that it prevents the empty-cart redirect once the order is finished)

  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives access to the state of the context (in this case the Store) and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const { cart, userInfo } = state; // gets the cart and userInfo out of the application state

  const [assets, setAssets] = useState({}); // Local state to store fetched data from IndexedDB

  const [orderingDisabled, setOrderingDisabled] = useState(null);

  const [holidayStatus, setHolidayStatus] = useState(null);
  const [loadingHolidayStatus, setLoadingHolidayStatus] = useState(true);
  const [holidayStatusError, setHolidayStatusError] = useState(false);
  const [agreedToHolidayDelay, setAgreedToHolidayDelay] = useState(false);

  const holidayAcknowledgementRequired =
    holidayConfirmationRequired(holidayStatus); // require the holiday confirmation during the final 7 days before the holiday and during the holiday itself

  const [shippingOk, setShippingOk] = useState(false);
  const [billingOk, setBillingOk] = useState(false);

  const shippingSameAsBilling = isSameAddress(
    cart.shippingAddress,
    cart.billingAddress
  ); // this will compare the shippingAddress and billingAddress for each key. Is true, if all values are the same.

  const preloadImage = (url) =>
    new Promise((resolve) => {
      if (!url) {
        // If there's no URL, resolve immediately to avoid stalling everything
        return resolve();
      }
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve(); // or you can reject(), but usually you still want to proceed
    }); // Helper: load a single URL and return a promise

  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer(); // usePayPalScriptReducer returns the state of loading the PayPal script (isPending) and the function to load the script (paypalDispatch)

  useEffect(() => {
    const fetchFlag = async () => {
      const { data } = await api.get('/api/settings/ordering');
      setOrderingDisabled(data.orderingDisabled);
    };
    fetchFlag();
  }, []); // fetch the orderingDisabled setting once on page load

  useEffect(() => {
    const fetchHolidayStatus = async () => {
      try {
        setHolidayStatusError(false);

        const { data } = await api.get('/api/settings/holiday'); // fetch the current holiday status from the backend

        setHolidayStatus(data); // save the returned holiday status
      } catch (err) {
        console.error('Failed to fetch holiday status:', err);

        setHolidayStatusError(true); // mark the holiday status request as failed
      } finally {
        setLoadingHolidayStatus(false); // finish loading after the request completes
      }
    }; // fetches and stores the current holiday status

    fetchHolidayStatus();
  }, []); // fetches the holiday status once when the component loads

  useEffect(() => {
    const shippingAddressOk = isAddressComplete(cart.shippingAddress);
    const billingAddressOk =
      shippingSameAsBilling || isAddressComplete(cart.billingAddress); // check if all required fields inside the billingAddress exist and are not empty

    setShippingOk(shippingAddressOk);
    setBillingOk(billingAddressOk);

    if (!userInfo) {
      navigate('/signup?redirect=/cart'); // if the user isn't signed in, he can't access the PlaceOrderScreen and instead will be redirected to the SigninScreen. After sign in, the user will than be redirected to the CartScreen
    } else if (!orderDone && cart.cartItems.length === 0) {
      navigate('/'); // this screen isn't accessable, if there are no items in the cart (the orderDone flag prevents the navigate('/') call after the order has been placed and the cart items are deleted)
    } else if (!shippingAddressOk || !billingAddressOk) {
      navigate('/shipping-address'); // the user must enter an address, before being able to place an order
    } else if (!cart.paymentMethod) {
      navigate('/payment'); // if the user hasn't selected a payment method, he will be navigated to the PaymentMethodScreen
    }

    const loadPaypalScript = async () => {
      const { data: clientId } = await api.get('/api/config/paypal'); // axios request to the backend, to get the paypal client id from the config file (authorization in the header, because it is an authorized request)
      paypalDispatch({
        type: 'resetOptions',
        value: {
          'client-id': clientId,
          currency: 'EUR',
        },
      }); // paypalDispatch in this case is used to cutomize its options (the value of type is 'resetOptions', because I set the value of 'client-id' and currency to my needs)
      paypalDispatch({ type: 'setLoadingStatus', value: 'pending' }); // paypalDispatch in this case is used to load the paypal script with the beforehand customized options
    }; // defines the function that loads the customized PayPal script into this Screen (this is needed, because I used the deferLoading prop in the PayPalScriptProvider component of index.js, that tells the component to delay the loading of the SDK until it is actually needed)
    loadPaypalScript(); // loads the customized PayPal script at the beginning of rendering this screen
  }, [
    userInfo,
    cart.cartItems.length,
    cart.paymentMethod,
    cart.shippingAddress,
    cart.billingAddress,
    shippingSameAsBilling,
    navigate,
    paypalDispatch,
  ]);

  const [loadingAssets, setLoadingAssets] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAssets = async () => {
      setLoadingAssets(true);
      setError(''); // a failure of an earlier run must not outlive this one

      try {
        const assetsMap = {};

        // 1) Build an array of *all* URLs to load
        const urlsToLoad = [];

        // 2) For items with uniqueCartId => fetch from IndexedDB
        for (const item of cart.cartItems) {
          if (item.uniqueCartId) {
            const data = await getFromIndexedDB(item.uniqueCartId);
            if (data) {
              // Store { pngDataUrl } for later usage
              assetsMap[item.uniqueCartId] = {
                pngDataUrl: data.pngDataUrl,
              };

              urlsToLoad.push(data.pngDataUrl); // Add the local PNG URL to the array of images to preload
            }
          }
        }

        // 3) For items with designId => fetch signedThumbnailUrl
        const designIds = cart.cartItems
          .filter((item) => item.designId)
          .map((item) => item.designId);

        if (designIds.length > 0) {
          try {
            // a) Fetch the signed thumbnail URLs
            const { data: signedUrls } = await api.post(
              '/api/designs/thumbnails',
              { designIds }
            );

            // b) Populate assetsMap with the returned signed URLs
            for (const designId of designIds) {
              const url = signedUrls.thumbnails?.[designId];
              if (url) {
                // Initialize if not present:
                if (!assetsMap[designId]) {
                  assetsMap[designId] = {};
                }
                assetsMap[designId].signedThumbnailUrl = url;

                urlsToLoad.push(url); // Add to preload array
              }
            }
          } catch (err) {
            // no error state here: the fallback URLs below are the handling, and an error set here replaces the order summary and the PayPal buttons with its message (until Oct 2026 it was the Error object itself, which blanked the page)
            console.error(
              'Failed to fetch design thumbnails or ownership:',
              err
            );
            // If there's an error, fall back to something safe
            for (const designId of designIds) {
              if (!assetsMap[designId]) {
                assetsMap[designId] = {};
              }
              const fallbackUrl = `https://${bucketName}.s3.eu-central-1.amazonaws.com/design-${designId}.png`;
              assetsMap[designId].signedThumbnailUrl = fallbackUrl;
              urlsToLoad.push(fallbackUrl);
            }
          }
        }

        // 4) Preload all images
        await Promise.all(urlsToLoad.map((url) => preloadImage(url)));

        // 5) Finally, store assetsMap, turn off loading
        setLoadingAssets(false);
        setAssets(assetsMap);
      } catch (error) {
        setLoadingAssets(false);
        setError(getError(error));
        console.error('Failed to fetch thumbnails:', error);
      }
    };

    fetchAssets();
  }, [cart.cartItems, userInfo]); // Fetch assets (pngDataUrl or signed thumbnail URL) for each cart item

  const [selectedZone, setSelectedZone] = useState({});
  const [selectedShippingCountry, setSelectedShippingCountry] = useState({});
  const [loadingZones, setLoadingZones] = useState(false);
  const [errorZones, setErrorZones] = useState(''); // set when the shipping zones could not be loaded at all
  const [zonesReloadCount, setZonesReloadCount] = useState(0); // bumped by the retry button to re-run the fetch below
  const countryIdShipping = cart.shippingAddress.countryId || '';

  useEffect(() => {
    const fetchZones = async () => {
      setLoadingZones(true);
      setErrorZones(''); // clear a previous failure, so a retry starts clean

      let zoneData;

      try {
        const { data } = await api.get('/api/zones?pageSize=1000'); // checkout needs every zone, not the first page — /api/zones defaults to 20 per page
        zoneData = data.zones;
      } catch (err) {
        // the request itself failed, so shipping costs cannot be calculated at all:
        console.error('Error fetching zones:', err);
        setErrorZones('place-order-screen-zones-error'); // store the key, not the translated text, so the message follows a language switch
        setLoadingZones(false);
        return;
      }

      try {
        if (countryIdShipping) {
          const match = findShippingZone(zoneData, countryIdShipping);

          if (match) {
            setSelectedZone(match.zone);
            setSelectedShippingCountry(match.country);
          } else {
            // No matched zone found:
            navigate('/shipping-address');

            toast.warning(
              t('place-order-screen-shipping-costs-calculation-warning')
            );
          }
        } else {
          // No countryId found in the users shippingAddress:
          navigate('/shipping-address');
          toast.warning(
            t('place-order-screen-shipping-costs-calculation-warning')
          );
        }

        setLoadingZones(false);
      } catch (err) {
        // the zones did arrive, so matching the user's country failed — treated like an unmatched country rather than blocking checkout:
        console.error('Error matching the shipping zone:', err);
        navigate('/shipping-address');
        toast.warning(
          t('place-order-screen-shipping-costs-calculation-warning')
        );
        setLoadingZones(false);
      }
    };
    fetchZones();
  }, [zonesReloadCount]); // fetch zone documents from the db and set selected zone, if countryId has been found in the users shippingAddress

  const [agreedToTerms1, setAgreedToTerms1] = useState(false);
  const [agreedToTerms2, setAgreedToTerms2] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };

  const handleClose = () => setShow(false);

  Object.assign(cart, calculateOrderTotals(cart.cartItems, selectedZone.price)); // sets the items, shipping and total amounts (gross, net, VAT) on the cart

  /* Currently hidded, as no vat is charged:
    const [showAdditionalItems, setShowAdditionalItems] = useState(false);

    const toggleAdditionalItems = () => {
      setShowAdditionalItems(!showAdditionalItems);
    }; // if the Order Total <ListGroup.Item> is clicked, two more ListGroup.Items will show up, displaying the totalNet and totalVat
  */

  async function checkCartIntegrity(cartItems) {
    const designIds = cartItems
      .filter((i) => i.designId)
      .map((i) => i.designId);

    if (designIds.length) {
      const { data } = await api.post('/api/designs/validate-for-purchase', {
        designIds,
      });

      if (!data.success) {
        const reasonKeys = getCartIntegrityReasonKeys(data.invalid); // deduplicate reasons so the same message is only shown once

        toast.error(reasonKeys.map((reasonKey) => t(reasonKey)).join('\n'));

        throw new Error(); // exit the createOrder function
      }
    }
  } // this function makes sure that all designs that are not owned by the customer are public and not deleted. It also makes sure that all products, that the designs are based on, are active.

  function handleSimpleStockFailure(simpleStockData, cartItems) {
    buildSimpleStockFailureMessages(simpleStockData, cartItems, t).forEach(
      (msg) => {
        toast.error(msg); // show the error messages one by one in a toast
      }
    );
  } // this function provides the user with matching error messages from the /simple-stock-check api

  function handleComplexStockFailure(stockData, cartItems) {
    buildComplexStockFailureMessages(stockData, cartItems, t).forEach((m) => {
      toast.error(m); // show the error messages one by one in a toast
    });
  } // This function creates a detailed error message about out of stock items in the cart

  const validateCurrentUserCanAccessCartUploadedIcons = async (cartItems) =>
    verifyUserIconAssetAccess({
      assetIds: await cartUserIconAssetIds(cartItems), // the uploaded icons the cart's customizer designs show (only cart items with a uniqueCartId were added directly from the Customizer and only these designs need to be checked for uploaded icons access rights)
      userInfo,
      t,
      loginRequiredKey: 'place-order-screen-uploaded-icons-login-required',
      unavailableKey: 'place-order-screen-uploaded-icons-wrong-account',
    }); // verifies that the signed-in user can access all uploaded icons used by designs in the indexedDB

  async function createOrder(data, actions) {
    try {
      // 1. check if ordering is enabled:
      const { data: flag } = await api.get('/api/settings/ordering');
      if (flag.orderingDisabled) {
        toast.error(t('place-order-screen-ordering-disabled-message-1'));
        return Promise.reject(
          new Error(t('place-order-screen-ordering-disabled-error'))
        );
      }

      // 2. Get the current holiday status again. This is deliberately checked immediately before creating the PayPal order in case the holiday phase changed while the customer had this page open:
      const { data: currentHolidayStatus } = await api.get(
        '/api/settings/holiday'
      );

      setHolidayStatus(currentHolidayStatus);

      const confirmationRequired =
        holidayConfirmationRequired(currentHolidayStatus);

      // 3. Require confirmation if orders are currently subject to post-holiday processing:
      if (confirmationRequired && !agreedToHolidayDelay) {
        toast.error(t('place-order-screen-holiday-confirmation-required'));
        return Promise.reject(
          new Error(t('place-order-screen-holiday-confirmation-required'))
        );
      }

      // 4. Double-check if the user has the permission to purchase the designs in the cart (if he does not own a design, it must be public and not deleted):
      await checkCartIntegrity(cart.cartItems);

      // 5. Check if the signed-in user can access all uploaded icons used by designs inside the indexedDB:
      const canAccessUploadedIcons =
        await validateCurrentUserCanAccessCartUploadedIcons(cart.cartItems);

      if (!canAccessUploadedIcons) {
        return Promise.reject(
          new Error(t('place-order-screen-uploaded-icons-wrong-account'))
        );
      }

      // 6. Summarize cart items by (layoutId, profile):
      const itemsForStockCheck = groupCartItemsByLayoutProfile(cart.cartItems);

      // 7. Perform a simple stock check:
      const { data: simpleStockData } = await api.post(
        '/api/layouts/simple-stock-check',
        { items: itemsForStockCheck }
      );

      // 8. Notify the user about out of stock items:
      if (!simpleStockData.success) {
        handleSimpleStockFailure(simpleStockData, cart.cartItems); // notify the user about out of stock items
        throw new Error(
          simpleStockData.message || t('place-order-screen-stock-check-failed')
        ); // throw an error so we stop here
      }

      // 9. Check stock for the layouts:
      const { data: complexStockData } = await api.post(
        '/api/layouts/complex-stock-check',
        { items: itemsForStockCheck }
      );

      // 10. Notify the user about out of stock items:
      if (!complexStockData.success) {
        handleComplexStockFailure(complexStockData, cart.cartItems); // notify the user about out of stock items
        throw new Error(
          complexStockData.message || t('place-order-screen-stock-check-failed')
        ); // throw an error so we stop here
      }

      // 11. Continue with creating the paypal order:
      return actions.order
        .create(paypalOrderBody(cart, selectedShippingCountry.countryCode)) // creates the paypal payment order over the cart's total and shipping address (utils/checkoutOrder.js)
        .then((orderID) => orderID); // if the paypal payment order was created successfully, than the orderID from PayPal will be returned
    } catch (err) {
      // If there's an error (e.g. when some items in the cart are out of stock):
      return Promise.reject(err); // return a rejected promise so we do not continue with PayPal
    }
  } // this function runs, when the PayPal (PayPal or Debit/Credit Card) button is clicked

  function onApprove(data, actions) {
    return actions.order.capture().then(async function (details) {
      try {
        dispatch({ type: 'PAY_REQUEST' });

        // Step 1: Reserve the stock (not only reserves the items, but also fails if the free stock isn't sufficient for this order - so it also serves simelar to a second "complex-stock-check" after the order has been payed)
        await api.post('/api/stock/reserve', { cartItems: cart.cartItems });

        // Step 2: the PayPal transaction id is read from the captured order when the order body is built (orderBody, utils/checkoutOrder.js)

        // Step 3: Generate the invoice number (only after successful payment):
        const {
          data: { invoiceNumber, invoiceDate },
        } = await api.get('/api/counters/generate/invoiceId');

        // Step 4: Save designs and thumbnails for each cart item
        const enrichedOrderItems = await Promise.all(
          cart.cartItems.map(async (item) => {
            if (item.uniqueCartId) {
              // If item has uniqueCartId => Fetch design and pngDataUrl from IndexedDB and save the design to the database:
              const asset = await getMigratedDesignAssetFromIndexedDB(
                item.uniqueCartId
              ); // loads the cart design from IndexedDB and migrates older saved design data if needed

              if (!asset) {
                throw Object.assign(
                  new Error(
                    'error-key: place-order-screen-indexeddb-item-fetch-failed'
                  ),
                  {
                    details: {
                      uniqueCartId: item.uniqueCartId,
                    },
                  }
                );
              }

              const { design, pngDataUrl } = asset; // Access the design field from IndexedDB

              if (!design || !pngDataUrl) {
                throw Object.assign(
                  new Error(
                    'error-key: place-order-screen-indexeddb-design-data-missing'
                  ),
                  {
                    details: {
                      uniqueCartId: item.uniqueCartId,
                    },
                  }
                );
              }

              const preparedDesign = migrateDesignIfNeeded(design); // migrate the design state to the latest customizerVersion

              // Generate PNG data URL for the thumbnail
              const thumbnailBlob = await fetch(pngDataUrl).then((res) =>
                res.blob()
              );

              const designToUpload = {
                ...preparedDesign, // Shallow copy of the design from IndexedDB
                keycapData: prepareKeycapDataForUpload(
                  preparedDesign.keycapData
                ), // Replace keycapData with the processed array
              };

              const formData = new FormData();
              formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
              formData.append('design', JSON.stringify(designToUpload)); // Pass the modified design state as JSON (using JSON.stringify() is necessary because FormData does not natively support appending complex objects like plain objects or arrays)
              formData.append('quantity', item.quantity); // Append the quantity of the item

              // Send the design and thumbnail to the backend
              const { data: savedDesign } = await api.post(
                '/api/designs/place-order',
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                }
              );

              return placedCartItem(item, savedDesign); // the cart item with the saved design's id and thumbnail in place of its IndexedDB reference (utils/checkoutOrder.js)
            } else {
              // It's a DB-based design that already exists
              try {
                await api.put(`/api/designs/increment-sales/${item.designId}`, {
                  quantity: item.quantity,
                }); // increment the numOfSales value for this design
              } catch (err) {
                console.error(
                  `Failed to increment sales for design ${item.designId}: ${err}`
                );
              }

              return item;
            }
          })
        );

        // Step 5 and 6: Create the order with the enriched order items (their line totals, the cart's totals and addresses, the PayPal capture, the browser's locale and time zone: orderBody, utils/checkoutOrder.js)
        const { data } = await api.post(
          '/api/orders',
          orderBody({
            cart,
            orderItems: enrichedOrderItems,
            invoiceNumber,
            invoiceDate,
            details,
            language: currentLanguage,
          })
        );

        // Step 7: Clear the cart and redirect
        ctxDispatch({ type: 'CART_CLEAR' }); // Clear cart in app state and localStorage
        dispatch({ type: 'PAY_SUCCESS' });
        toast.success(t('place-order-screen-success-1'));

        setOrderDone(true); // this is to prevent a second navigate() call to '/', because the cart items are cleared and the useEffect from above runs after that navigate() call in the next line
        navigate(`/order/${data.order.orderId}`);
      } catch (err) {
        const errorMessage = getError(err);
        const errorDetails = err?.response?.data?.details || err?.details;

        dispatch({ type: 'PAY_FAIL', payload: errorMessage });

        toast.error(t(errorMessage, { details: errorDetails })); // every rejection of the try block carries a message (an axios error through getError, the thrown error keys, the PNG fetch's TypeError), so the fallback for an empty one went with its key (Oct 2026, user decision); a declined instrument never reaches this catch, as the SDK restarts the checkout itself (PayPal's funding-failures guide)
      }
    });
  } // this function runs, when the paypal payment (paypal or debit/credit card) was successful. This will create/post a new order to the backend, containing all the order information. (The paymentResult information of the order is filled with information from the details objcet. This object holds the user and payment information from paypal)

  return (
    <Container className="checkout-steps-container">
      {/* implements the custom CheckoutSteps component with 'step1', 'step2', 'step3' and 'step4' as active: */}
      <CheckoutSteps step1 step2 step3 step4></CheckoutSteps>

      <title>{t('place-order-screen-helmet')}</title>

      <h1 className="screen-heading">{t('place-order-screen-heading')}</h1>

      {loading || loadingAssets || loadingZones || isPending ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : errorZones ? (
        <div className="zones-error-container">
          <MessageBox className="danger-alert">{t(errorZones)}</MessageBox>

          <Button
            variant="dark"
            type="button"
            onClick={() => setZonesReloadCount((count) => count + 1)}
          >
            {t('general-try-again')}
          </Button>
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <div className="order-summary-container">
          <Modal
            show={show}
            onHide={handleClose}
            centered
            className="thumbnail-modal"
          >
            <Modal.Body>
              <img
                src={selectedThumbnail}
                alt={t('place-order-screen-selected-thumbnail')}
              />
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                {t('place-order-screen-close')}
              </Button>
            </Modal.Footer>
          </Modal>

          <div className="address-payment-items-container">
            <Card className="order-card">
              <Card.Body>
                <Card.Title>
                  {t(
                    shippingSameAsBilling
                      ? 'place-order-screen-shipping-and-billing-address'
                      : 'place-order-screen-shipping-address'
                  )}

                  <Button
                    variant="none"
                    className="no-css"
                    href="/shipping-address"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      className="bi bi-pencil-square"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                      <path
                        fillRule="evenodd"
                        d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                      />
                    </svg>
                  </Button>
                </Card.Title>

                <div className="divider"></div>

                <Card.Text>
                  {cart.shippingAddress.companyName ? (
                    <>
                      <span>{cart.shippingAddress.companyName}</span>
                      <br />
                    </>
                  ) : null}
                  <span>{cart.shippingAddress.fullName}</span>
                  <br />
                  <span>{cart.shippingAddress.email}</span>
                  <br />
                  <span>{cart.shippingAddress.phone}</span>
                  <br />
                  <span>
                    {cart.shippingAddress.street +
                      ' ' +
                      cart.shippingAddress.houseNumber}
                    , {cart.shippingAddress.postalCode}{' '}
                    {cart.shippingAddress.city}, {cart.shippingAddress.country}
                  </span>
                </Card.Text>
              </Card.Body>
            </Card>

            {!shippingSameAsBilling && (
              <Card className="order-card">
                <Card.Body>
                  <Card.Title>
                    {t('place-order-screen-billing-address')}

                    <Button
                      variant="none"
                      className="no-css"
                      href="/shipping-address"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        className="bi bi-pencil-square"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                        <path
                          fillRule="evenodd"
                          d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                        />
                      </svg>
                    </Button>
                  </Card.Title>

                  <div className="divider"></div>

                  <Card.Text>
                    {cart.billingAddress.companyName ? (
                      <>
                        <span>{cart.billingAddress.companyName}</span>
                        <br />
                      </>
                    ) : null}
                    <span>{cart.billingAddress.fullName}</span>
                    <br />
                    <span>{cart.billingAddress.email}</span>
                    <br />
                    <span>{cart.billingAddress.phone}</span>
                    <br />
                    <span>
                      {cart.billingAddress.street +
                        ' ' +
                        cart.billingAddress.houseNumber}
                      , {cart.billingAddress.postalCode}{' '}
                      {cart.billingAddress.city}, {cart.billingAddress.country}
                    </span>
                  </Card.Text>
                </Card.Body>
              </Card>
            )}

            <Card className="order-card">
              <Card.Body>
                <Card.Title>
                  {t('place-order-screen-payment')}

                  <Button variant="none" className="no-css" href="/payment">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      className="bi bi-pencil-square"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                      <path
                        fillRule="evenodd"
                        d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                      />
                    </svg>
                  </Button>
                </Card.Title>

                <div className="divider"></div>

                {cart.paymentMethod === 'paypal' ? (
                  <div>
                    <img
                      className="order-card-paypal-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/PayPal.png"
                      alt="PayPal"
                    />
                  </div>
                ) : cart.paymentMethod === 'card' ? (
                  <div>
                    <img
                      className="order-card-master-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/Mastercard.png"
                      alt="Mastercard"
                    />
                    <img
                      className="order-card-visa-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/Visa.png"
                      alt="Visa"
                    />
                    <img
                      className="order-card-amex-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/AmericanExpress.png"
                      alt="AmericanExpress"
                    />
                  </div>
                ) : (
                  <Card.Text>{cart.paymentMethod}</Card.Text>
                )}
              </Card.Body>
            </Card>

            <Card className="order-card">
              <Card.Body>
                <Card.Title>
                  {t('place-order-screen-items')}

                  <Button variant="none" className="no-css" href="/cart">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      className="bi bi-pencil-square"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                      <path
                        fillRule="evenodd"
                        d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                      />
                    </svg>
                  </Button>
                </Card.Title>

                <div className="d-sm-none divider"></div>

                <ListGroup viriant="flush">
                  <ListGroup.Item className="d-none d-sm-flex order-card-items-table-header order-card-table-header-divider">
                    <div className="order-card-items-table-image-heading">
                      {t('place-order-screen-products-image')}
                    </div>

                    <div className="order-card-items-table-name-heading">
                      {t('place-order-screen-products-name')}
                    </div>

                    <div className="order-card-items-table-quantity-heading">
                      {t('place-order-screen-products-quantity')}
                    </div>

                    <div className="order-card-items-table-price-heading">
                      {t('place-order-screen-products-price')}
                    </div>
                  </ListGroup.Item>

                  {cart.cartItems.map((item, index) => (
                    <div key={index}>
                      {index !== 0 && <div className="divider"></div>}

                      <ListGroup.Item className="order-card-items-table-body">
                        <div className="order-card-items-table-image">
                          {/* the thumbnail sits in a button so the enlarged view is reachable by keyboard (the button takes its accessible name from the image's alt text): */}
                          <button
                            type="button"
                            className="a11y-unstyled-button"
                            onClick={() =>
                              handleShow(
                                item.uniqueCartId
                                  ? assets[item.uniqueCartId]?.pngDataUrl
                                  : assets[item.designId]?.signedThumbnailUrl
                              )
                            }
                          >
                            <img
                              className="img-fluid img-thumbnail-order-screen"
                              src={
                                item.uniqueCartId
                                  ? assets[item.uniqueCartId]?.pngDataUrl
                                  : assets[item.designId]?.signedThumbnailUrl
                              }
                              alt={item.name}
                            />
                          </button>
                        </div>

                        <div className="order-card-name-quantity-price-wrapper">
                          <div className="order-card-items-table-name">
                            {item.name}
                          </div>

                          <div className="order-card-items-table-quantity">
                            <span>
                              {t('place-order-screen-products-quantity')}:
                            </span>
                            {item.quantity}
                          </div>

                          <div className="order-card-items-table-price">
                            <span>
                              {t('place-order-screen-products-price')}:
                            </span>
                            {formatEuro(item.unitGross)}
                            {/*
                              <div className="incl-vat">
                                {t('place-order-screen-vat-1', {
                                  vatRate: item.vatRate,
                                })}
                              </div>
                              */}
                          </div>
                        </div>
                      </ListGroup.Item>
                    </div>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </div>

          <div className="payment-container">
            <Card className="order-card">
              <Card.Body>
                <div className="order-card-subtotal">
                  <div>{t('place-order-screen-subtotal')}</div>
                  <div>{formatEuro(cart.itemsGross)}</div>
                </div>

                <div className="order-card-shipping">
                  <div>{t('place-order-screen-shipping')}</div>
                  <div>
                    {cart?.shippingGross
                      ? formatEuro(cart.shippingGross)
                      : formatEuro(0)}
                  </div>
                </div>

                <div className="order-card-total">
                  <div>{t('place-order-screen-total')}</div>
                  <div>{formatEuro(cart.totalGross)}</div>
                </div>

                {/* 
                    <Row>
                      <Col>
                        <span className="ms-1 incl-vat">
                          {t('place-order-screen-vat-2')}
                        </span>
                        <Button
                          variant="dark"
                          size="sm"
                          type="button"
                          style={{ marginLeft: '5px' }}
                          onClick={toggleAdditionalItems}
                        >
                          {showAdditionalItems ? (
                            // changes the icon, when the Button is clicked (arrow down icon vs. arrow up icon)
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-chevron-up"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-chevron-down"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                              />
                            </svg>
                          )}
                        </Button>
                      </Col>
                    </Row>
                */}

                <div className="order-card-no-vat-charged">
                  {t('order-screen-no-vat-charged')}
                </div>

                {/*
                  {showAdditionalItems && (
                    // if showAdditionalItems is true (so when the Order Total ListGroup.Item is clicked), the following ListGroup.Items will be shown
                    <>
                      <ListGroup.Item>
                        <Row>
                          <Col>{t('place-order-screen-total-net')}</Col>
                          <Col>{formatEuro(cart.totalNet)}</Col>
                        </Row>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <Row>
                          <Col>{t('place-order-screen-vat-3')}</Col>
                          <Col>{formatEuro(cart.totalVat)}</Col>
                        </Row>
                      </ListGroup.Item>
                    </>
                  )}
                */}

                {!orderingDisabled && holidayAcknowledgementRequired && (
                  <Form.Group
                    className="order-card-accept-holiday-delay"
                    controlId="acceptHolidayDelay"
                  >
                    <Form.Check
                      type="checkbox"
                      label={
                        <>
                          {t('place-order-screen-accept-holiday-delay', {
                            resumeDate: formatDate(
                              holidayStatus.processingResumeDate,
                              {
                                dateOnly: true,
                              }
                            ),
                          })}
                          <span style={{ color: '#fe4a49' }}> *</span>
                        </>
                      }
                      onChange={(event) =>
                        setAgreedToHolidayDelay(event.target.checked)
                      }
                      checked={agreedToHolidayDelay}
                    />
                  </Form.Group>
                )}

                {!orderingDisabled && (
                  <Form.Group
                    className={`order-card-accept-terms-1 ${
                      holidayAcknowledgementRequired
                        ? 'order-card-accept-terms-1-holiday'
                        : ''
                    }`}
                    controlId="acceptTerms1"
                  >
                    <Form.Check
                      type="checkbox"
                      label={
                        <>
                          {t('signup-screen-accept-terms')}
                          <span style={{ color: '#fe4a49' }}> *</span>
                        </>
                      }
                      onChange={() => setAgreedToTerms1(!agreedToTerms1)}
                      checked={agreedToTerms1 === true}
                    />
                  </Form.Group>
                )}

                {!orderingDisabled && (
                  <Form.Group
                    className="order-card-accept-terms-2"
                    controlId="acceptTerms2"
                  >
                    <Form.Check
                      type="checkbox"
                      label={
                        <>
                          {t('signup-screen-accept-terms-1')}{' '}
                          <Link
                            className="text-link"
                            to={`/general-terms-and-conditions`}
                          >
                            {t('signup-screen-accept-terms-2')}
                          </Link>
                          ,{' '}
                          <Link
                            className="text-link"
                            to={`/right-of-withdrawal`}
                          >
                            {t('signup-screen-accept-terms-5')}
                          </Link>{' '}
                          {t('signup-screen-accept-terms-3')}{' '}
                          <Link className="text-link" to={`/privacy-policy`}>
                            {t('signup-screen-accept-terms-4')}
                          </Link>
                          {'.'}
                          <span style={{ color: '#fe4a49' }}> *</span>
                        </>
                      }
                      onChange={() => setAgreedToTerms2(!agreedToTerms2)}
                      checked={agreedToTerms2 === true}
                    />
                  </Form.Group>
                )}

                <div className="order-card-checkout-container">
                  {orderingDisabled ? (
                    <Alert variant="warning">
                      {t('place-order-screen-ordering-disabled-message-2')}
                    </Alert>
                  ) : holidayStatusError ? (
                    <Alert variant="warning">
                      {t('place-order-screen-holiday-status-error')}
                    </Alert>
                  ) : shippingOk && billingOk ? (
                    <PayPalButtons
                      fundingSource={cart.paymentMethod}
                      disabled={
                        cart.cartItems.length === 0 ||
                        !agreedToTerms1 ||
                        !agreedToTerms2 ||
                        orderingDisabled ||
                        loadingHolidayStatus ||
                        holidayStatusError ||
                        (holidayAcknowledgementRequired &&
                          !agreedToHolidayDelay)
                      }
                      onInit={(_, actions) => {
                        if (orderingDisabled) actions.disable();
                      }} // onInit runs when the button is first initialized/rendered (If ordering is currently disabled when the PayPal button loads, immediately disable the button)
                      onClick={(_, actions) => {
                        // last-second check in case the orderingDisabled flag flipped
                        if (orderingDisabled) {
                          toast.error(
                            t('place-order-screen-ordering-disabled-message-1')
                          );
                          return actions.reject(); // cancels the checkout immediately
                        }
                        return actions.resolve(); // continues to createOrder()
                      }}
                      createOrder={createOrder} // this function runs when the button is clicked
                      onApprove={onApprove} // this function runs when the payment was successful
                      onError={async (err) => {
                        console.log(getError(err));
                      }} // triggered when something goes wrong during the PayPal flow (when the payment fails unexpectedly)
                    />
                  ) : (
                    <p>{t('place-order-screen-payment-message')}</p>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </Container>
  );
}
